import { useState } from 'react';
import { Image, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { Link } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../src/context/AuthContext';
import { useThemeMode } from '../../src/context/ThemeContext';
import { Button, ErrorText, FormInput, Label } from '../../src/components/ui';
import { colors, fonts, minSafeArea, radii } from '../../src/theme';
import { ApiError } from '../../src/api/client';
import { PosterKind, Role } from '../../src/types';

// Recolored per theme so the mark's own gradient matches whichever palette
// the user picked — only app.json's static "icon" (the OS home-screen icon,
// which can't be theme-aware) still points at the plain light version.
const LOGO_BY_MODE = {
  light: require('../../assets/logo-light.png'),
  dark: require('../../assets/logo-dark.png'),
  mocha: require('../../assets/logo-mocha.png'),
};

export default function RegisterScreen() {
  const { register } = useAuth();
  const { mode } = useThemeMode();
  const insets = useSafeAreaInsets();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('REVIEWER');
  const [posterKind, setPosterKind] = useState<PosterKind>('INDIVIDUAL');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setError(null);
    setLoading(true);
    try {
      await register(
        email.trim().toLowerCase(),
        password,
        name.trim(),
        role,
        role === 'SHOP_OWNER' ? posterKind : undefined,
        referralCode.trim() || undefined,
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Đăng ký thất bại');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView
      style={{ backgroundColor: colors.background }}
      contentContainerStyle={{
        flexGrow: 1,
        justifyContent: 'center',
        padding: 16,
        paddingTop: Math.max(insets.top, minSafeArea.top) + 16,
        paddingBottom: Math.max(insets.bottom, minSafeArea.bottom) + 16,
        ...(Platform.OS === 'web' ? { alignItems: 'center' } : null),
      }}
    >
      <View style={Platform.OS === 'web' ? { width: '100%', maxWidth: 480 } : undefined}>
      <View style={{ alignItems: 'center', marginBottom: 28 }}>
        <Image
          source={LOGO_BY_MODE[mode]}
          style={{ width: 56, height: 56, borderRadius: 16, marginBottom: 10 }}
        />
      </View>

      <Text
        style={{
          fontSize: 26,
          fontFamily: fonts.bold,
          color: colors.text,
          marginBottom: 6,
          letterSpacing: -0.4,
        }}
      >
        Tạo tài khoản
      </Text>
      <Text style={{ color: colors.textMuted, fontFamily: fonts.regular, fontSize: 14, marginBottom: 20 }}>
        Bạn là ai?
      </Text>

      <View style={{ flexDirection: 'row', gap: 10, marginBottom: 8 }}>
        <RoleOption
          label="Người nhận việc"
          selected={role === 'REVIEWER'}
          onPress={() => setRole('REVIEWER')}
        />
        <RoleOption
          label="Người đăng việc"
          selected={role === 'SHOP_OWNER'}
          onPress={() => setRole('SHOP_OWNER')}
        />
      </View>
      <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: role === 'SHOP_OWNER' ? 12 : 20 }}>
        Sau này bạn có thể thêm vai trò còn lại ngay trong ứng dụng, không cần đăng ký tài khoản mới.
      </Text>

      {role === 'SHOP_OWNER' ? (
        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 20 }}>
          <SubOption
            label="Cá nhân"
            selected={posterKind === 'INDIVIDUAL'}
            onPress={() => setPosterKind('INDIVIDUAL')}
          />
          <SubOption
            label="Doanh nghiệp"
            selected={posterKind === 'BUSINESS'}
            onPress={() => setPosterKind('BUSINESS')}
          />
        </View>
      ) : null}

      <Label>Họ tên</Label>
      <FormInput value={name} onChangeText={setName} placeholder="Nguyễn Văn A" />

      <Label>Email</Label>
      <FormInput
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        keyboardType="email-address"
        placeholder="you@example.com"
      />

      <Label>Mật khẩu</Label>
      <FormInput
        value={password}
        onChangeText={setPassword}
        secureTextEntry
        placeholder="Tối thiểu 6 ký tự"
      />

      <Label>Mã giới thiệu (nếu có)</Label>
      <FormInput
        value={referralCode}
        onChangeText={setReferralCode}
        autoCapitalize="none"
        placeholder="Bỏ trống nếu không có"
      />

      <ErrorText>{error}</ErrorText>

      <View style={{ marginTop: 4 }}>
        <Button
          title="Đăng ký"
          onPress={onSubmit}
          loading={loading}
          disabled={!email || !password || !name}
        />
      </View>

      <View style={{ flexDirection: 'row', justifyContent: 'center', marginTop: 22, gap: 4 }}>
        <Text style={{ color: colors.textMuted, fontFamily: fonts.regular, fontSize: 14 }}>
          Đã có tài khoản?
        </Text>
        <Link
          href="/(auth)/login"
          style={{ color: colors.primary, fontFamily: fonts.semiBold, fontSize: 14 }}
        >
          Đăng nhập
        </Link>
      </View>
      </View>
    </ScrollView>
  );
}

function RoleOption({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex: 1,
        borderWidth: 1.5,
        borderColor: selected ? colors.primary : colors.border,
        backgroundColor: selected ? colors.primarySoft : colors.card,
        borderRadius: radii.md,
        paddingVertical: 15,
        alignItems: 'center',
      }}
    >
      <Text
        style={{
          color: selected ? colors.primaryDark : colors.text,
          fontFamily: fonts.semiBold,
          fontSize: 14,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function SubOption({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex: 1,
        borderWidth: 1,
        borderColor: selected ? colors.primary : colors.border,
        backgroundColor: selected ? colors.primarySoft : colors.card,
        borderRadius: radii.sm,
        paddingVertical: 10,
        alignItems: 'center',
      }}
    >
      <Text
        style={{
          color: selected ? colors.primaryDark : colors.textMuted,
          fontFamily: fonts.medium,
          fontSize: 12.5,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}
